/**
 * Diff Highlighting Module for Google Sheets
 *
 * Applies visual highlighting to show differences between a snapshot and current state.
 * Uses conditional formatting to preserve gridlines.
 * Manages state to restore original formatting when highlights are cleared.
 */

import { callAppsScript } from '../agents/sheets-tools'
import {
  columnLetterToNumber,
  columnNumberToLetter,
  parseCellAddress,
} from '../agents/tool-utils'
import type { CellDiff, Diff } from './diff'

// ============================================================================
// Types
// ============================================================================

interface HighlightState {
  originalTabColors: Map<string, string | null>
  highlightedSheets: Set<string> // Sheet names that have highlights
  isActive: boolean
}

interface Rectangle {
  startCol: number
  endCol: number
  startRow: number
  endRow: number
}

// ============================================================================
// Rectangle Grouping Algorithm
// ============================================================================

/**
 * Build optimized address groups by merging adjacent cells into rectangles.
 * This dramatically reduces the number of conditional format rules needed.
 */
function buildAddressGroups(addresses: string[]): string[] {
  if (addresses.length === 0) return []
  if (addresses.length === 1) return addresses

  // Parse all addresses to coordinates
  const coords = addresses.map((addr) => {
    const { col, row } = parseCellAddress(addr)
    return { col: columnLetterToNumber(col), row }
  })

  // Sort by row, then by column
  coords.sort((a, b) => a.row - b.row || a.col - b.col)

  // Step 1: Build horizontal segments for each row
  const segments: Array<{ row: number; startCol: number; endCol: number }> = []
  let currentSegment: { row: number; startCol: number; endCol: number } | null =
    null

  for (const coord of coords) {
    if (
      currentSegment &&
      currentSegment.row === coord.row &&
      coord.col === currentSegment.endCol + 1
    ) {
      // Extend current segment
      currentSegment.endCol = coord.col
    } else {
      // Start new segment
      if (currentSegment) {
        segments.push(currentSegment)
      }
      currentSegment = {
        row: coord.row,
        startCol: coord.col,
        endCol: coord.col,
      }
    }
  }
  if (currentSegment) {
    segments.push(currentSegment)
  }

  // Step 2: Merge segments vertically into rectangles
  const rectangles: Rectangle[] = []
  const openRects: Rectangle[] = []

  for (const segment of segments) {
    // Try to extend an open rectangle
    let merged = false
    for (let i = 0; i < openRects.length; i++) {
      const rect = openRects[i]
      if (
        rect.startCol === segment.startCol &&
        rect.endCol === segment.endCol &&
        rect.endRow + 1 === segment.row
      ) {
        // Extend rectangle downward
        rect.endRow = segment.row
        merged = true
        break
      }
    }

    if (!merged) {
      // Close any open rectangles that can't continue
      for (let i = openRects.length - 1; i >= 0; i--) {
        const rect = openRects[i]
        if (rect.endRow < segment.row - 1) {
          rectangles.push(rect)
          openRects.splice(i, 1)
        }
      }
      // Start a new rectangle
      openRects.push({
        startCol: segment.startCol,
        endCol: segment.endCol,
        startRow: segment.row,
        endRow: segment.row,
      })
    }
  }

  // Close remaining open rectangles
  rectangles.push(...openRects)

  // Convert rectangles to A1 notation strings
  return rectangles.map((rect) => {
    const startAddr = `${columnNumberToLetter(rect.startCol)}${rect.startRow}`
    if (rect.startCol === rect.endCol && rect.startRow === rect.endRow) {
      return startAddr
    }
    const endAddr = `${columnNumberToLetter(rect.endCol)}${rect.endRow}`
    return `${startAddr}:${endAddr}`
  })
}

// ============================================================================
// Color Constants
// ============================================================================

const COLORS = {
  added: '#C6EFCE', // Light green
  deleted: '#FFC7CE', // Light red
  'formula-result-changed': '#FFFF99', // Light yellow
  'value-changed': '#FFEB9C', // Light orange
  sheetTab: '#FF9900', // Orange (for sheets with changes)
  sheetTabAdded: '#00B050', // Green (for added sheets)
} as const

type DiffColorType =
  | 'added'
  | 'deleted'
  | 'formula-result-changed'
  | 'value-changed'

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Get the highlight color for a diff type
 */
function getColorForDiffType(type: DiffColorType): string {
  switch (type) {
    case 'added':
      return COLORS.added
    case 'deleted':
      return COLORS.deleted
    case 'formula-result-changed':
      return COLORS['formula-result-changed']
    case 'value-changed':
      return COLORS['value-changed']
  }
}

/**
 * Group cells by their diff type for batch processing
 */
function groupCellsByType(
  cells: CellDiff[]
): Map<CellDiff['type'], CellDiff[]> {
  const groups = new Map<CellDiff['type'], CellDiff[]>()
  for (const cell of cells) {
    const existing = groups.get(cell.type)
    if (existing) {
      existing.push(cell)
    } else {
      groups.set(cell.type, [cell])
    }
  }
  return groups
}

// ============================================================================
// DiffHighlighter Class
// ============================================================================

class DiffHighlighter {
  private state: HighlightState = {
    originalTabColors: new Map(),
    highlightedSheets: new Set(),
    isActive: false,
  }

  /**
   * Apply highlights to show differences
   */
  async applyHighlights(diff: Diff): Promise<void> {
    // Clear any existing highlights first
    if (this.state.isActive) {
      await this.clearHighlights()
    }

    const highlights: Array<{
      sheetName: string
      rangeAddress: string
      color: string
    }> = []

    // Build list of highlights to apply
    for (const sheetDiff of diff.sheets) {
      if (!sheetDiff.hasChanges) continue

      this.state.highlightedSheets.add(sheetDiff.sheetName)

      // Group cells by diff type, then build rectangles for each type
      const cellsByType = groupCellsByType(sheetDiff.cells)

      for (const [diffType, cells] of Array.from(cellsByType)) {
        if (diffType === 'unchanged') continue

        const addresses = cells.map((c) => c.address)
        const rectangles = buildAddressGroups(addresses)
        const color = getColorForDiffType(diffType)

        for (const rangeAddress of rectangles) {
          highlights.push({
            sheetName: sheetDiff.sheetName,
            rangeAddress,
            color,
          })
        }
      }
    }

    // Apply all highlights via Apps Script
    if (highlights.length > 0) {
      await callAppsScript('applySnapshotHighlights', highlights)
      console.log(`Applied ${highlights.length} conditional format rectangles`)
    }

    // Handle tab colors for sheets with changes
    const addedSheetNames = new Set(diff.addedSheets.map((s) => s.name))

    for (const sheetDiff of diff.sheets) {
      if (addedSheetNames.has(sheetDiff.sheetName)) {
        // Get original tab color before setting
        const originalColor = await callAppsScript<{
          success: boolean
          color: string | null
        }>('getSheetTabColor', sheetDiff.sheetName)

        this.state.originalTabColors.set(
          sheetDiff.sheetName,
          originalColor.color
        )

        // Set green for added sheets
        await callAppsScript(
          'setSheetTabColor',
          sheetDiff.sheetName,
          COLORS.sheetTabAdded
        )
      } else if (sheetDiff.hasChanges) {
        // Get original tab color before setting
        const originalColor = await callAppsScript<{
          success: boolean
          color: string | null
        }>('getSheetTabColor', sheetDiff.sheetName)

        this.state.originalTabColors.set(
          sheetDiff.sheetName,
          originalColor.color
        )

        // Set orange for modified sheets
        await callAppsScript(
          'setSheetTabColor',
          sheetDiff.sheetName,
          COLORS.sheetTab
        )
      }
    }

    this.state.isActive = true
    console.log('Applied diff highlights to Google Sheets')
  }

  /**
   * Clear all highlights and restore original formatting
   */
  async clearHighlights(): Promise<void> {
    if (!this.state.isActive) return

    // Clear all conditional formatting from highlighted sheets
    const sheetNames = Array.from(this.state.highlightedSheets)
    if (sheetNames.length > 0) {
      await callAppsScript('clearSnapshotHighlights', sheetNames)
    }

    // Restore original tab colors
    for (const [sheetName, originalColor] of Array.from(
      this.state.originalTabColors
    )) {
      await callAppsScript('setSheetTabColor', sheetName, originalColor || null)
    }

    // Reset state
    this.state = {
      originalTabColors: new Map(),
      highlightedSheets: new Set(),
      isActive: false,
    }

    console.log('Cleared diff highlights from Google Sheets')
  }

  /**
   * Check if highlighting is currently active
   */
  isComparing(): boolean {
    return this.state.isActive
  }
}

// ============================================================================
// Export Singleton Instance
// ============================================================================

export const diffHighlighter = new DiffHighlighter()
